const API_URL = import.meta.env.VITE_API_URL;

interface RequestOptions extends RequestInit {
  params?: Record<string, any>;
}

class ApiClient {
  private async request(endpoint: string, options: RequestOptions = {}) {
    const { params, headers, body, ...rest } = options;
    
    // Add query parameters to URL
    let url = `${API_URL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    
    // Default headers
    const defaultHeaders: Record<string, string> = {};
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Don't set Content-Type if body is FormData (browser will set it with boundary)
    if (!(body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...rest,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
    });

    // Handle 401 Unauthorized
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      // Potential improvement: trigger a global event or callback or just reload
      // window.location.href = '/login'; 
    }

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  get(endpoint: string, params?: Record<string, any>, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'GET', params });
  }

  post(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  put(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  patch(endpoint: string, body?: any, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  delete(endpoint: string, options?: RequestOptions) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient();
